import React, { useEffect, useMemo, useRef, useState } from 'react';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import { buttonClass } from '../components/ui/Button';
import { TextArea } from '../components/ui/Field';
import { useMention } from '../components/ui/ComboboxMention';
import Tooltip from '../components/ui/Tooltip';
import { useToast } from '../components/Toast/ToastContext';
import { getFullDateTime, parseTimestamp } from '../utils/date-utils';
import * as apiPoints from './apiPoints';
import { errorMessage } from './api';
import { canManage } from './permissions';
import VetoComposer, { FlagIcon } from './VetoComposer';
import type { PostThreadInput, ThreadItem } from './pointsTypes';
import type { PlanMember, PlanRole } from './types';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';

const KEY = buttonClass('quiet', 'sm');
const SEND = buttonClass('tonal', 'sm');

const LIFT_BODY = 'Lifted the veto.';

const RelativeTime: React.FC<{ iso: string }> = ({ iso }) => {
  const d = parseTimestamp(iso);
  if (!d) return null;
  return (
    <span className="tabular-nums" title={getFullDateTime(iso)}>
      {d.fromNow()}
    </span>
  );
};

const nameOf = (m: Pick<PlanMember, 'firstname' | 'lastname'>) => `${m.firstname || ''}${m.lastname ? ` ${m.lastname}` : ''}`.trim();

// ── The composer ────────────────────────────────────────────────────────────
//
// The @-mention picker is ui/useMention. The people offered are this plan's members, not every
// user: the notifier drops anyone who cannot open the plan, so offering them would @ someone who
// hears nothing. A mention counts only while its "@Name" is still in the text, so deleting the
// name drops it without a separate chip to remove.

interface ComposerProps {
  label: string;
  placeholder: string;
  members: PlanMember[];
  userId: string | null;
  autoFocus?: boolean;
  submitLabel: string;
  onPost: (body: string, mentionIds: string[]) => Promise<boolean>;
  onCancel?: () => void;
}

const Composer: React.FC<ComposerProps> = ({ label, placeholder, members, userId, autoFocus, submitLabel, onPost, onCancel }) => {
  const [draft, setDraft] = useState('');
  const [picked, setPicked] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  // A reply that lands closes this composer from inside onPost.
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  const people = useMemo(() => members.filter((m) => m.user_id !== userId), [members, userId]);

  const options = useMemo(() => people.map((m) => ({ id: m.user_id, label: nameOf(m) })), [people]);
  const mention = useMention({
    inputRef: ref,
    value: draft,
    onChange: setDraft,
    options,
    insert: (o) => `@${o.label} `,
    onPick: (o) => setPicked((p) => (p.some((x) => x.id === o.id) ? p : [...p, { id: String(o.id), name: o.label }])),
    side: 'above',
    label: 'People on this plan',
  });

  const post = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    const ids = picked.filter((p) => text.includes(`@${p.name}`)).map((p) => p.id);
    const ok = await onPost(text, ids);
    if (!mounted.current) return;
    setBusy(false);
    if (ok) {
      setDraft('');
      setPicked([]);
      // After the re-render that re-enables the field.
      requestAnimationFrame(() => ref.current?.focus());
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    mention.fieldProps.onKeyDown(e);
    if (e.defaultPrevented) return;
    if (e.key === 'Escape' && onCancel) {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      post();
    }
  };

  return (
    <div className="flex items-end gap-1.5">
      <div className="relative min-w-0 flex-1">
        <TextArea
          ref={ref}
          value={draft}
          rows={1}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          {...mention.fieldProps}
          onKeyDown={onKeyDown}
          aria-label={label}
          placeholder={placeholder}
          className="min-h-[36px]"
        />
        {mention.list}
      </div>
      <button type="button" onClick={post} disabled={busy || !draft.trim()} className={SEND}>
        {submitLabel}
      </button>
      {onCancel && (
        <button type="button" onClick={onCancel} disabled={busy} className={KEY}>
          Cancel
        </button>
      )}
    </div>
  );
};

// ── One message ─────────────────────────────────────────────────────────────

const Byline: React.FC<{ item: ThreadItem }> = ({ item }) => (
  <div className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-on-surface-variant">
    <span className="font-semibold text-on-surface">{item.user_name ?? 'Former member'}</span>
    <RelativeTime iso={item.created_at} />
  </div>
);

interface VetoRowProps {
  item: ThreadItem;
  liftedBy: string | null | undefined;
  lifted: boolean;
  canLift: boolean;
  onLift: () => Promise<void>;
}

const VetoRow: React.FC<VetoRowProps> = ({ item, liftedBy, lifted, canLift, onLift }) => {
  const [busy, setBusy] = useState(false);
  if (lifted) {
    return (
      <div className="flex gap-2 rounded-md px-2 py-1.5">
        <FlagIcon className="mt-0.5 h-4 w-4 text-on-surface-variant" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-on-surface-variant">
            <span className="font-semibold">Veto from {item.user_name ?? 'a former member'}</span>, lifted
            {liftedBy ? ` by ${liftedBy}` : ''}
          </div>
          <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-on-surface-variant line-through decoration-outline">
            {item.body}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-2 rounded-md bg-red-500/10 px-2 py-1.5">
      <FlagIcon className="mt-0.5 h-4 w-4 text-red-300" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 text-[11px]">
          <span className="font-semibold text-red-300">Veto from {item.user_name ?? 'a former member'}</span>
          <span className="text-on-surface-variant">
            <RelativeTime iso={item.created_at} />
          </span>
        </div>
        <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-on-surface">{item.body}</p>
        {canLift && (
          <Tooltip label="Withdraw this objection. The veto stays in the thread, struck through.">
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                await onLift();
                setBusy(false);
              }}
              className={`${KEY} -ml-2 mt-0.5`}
            >
              Lift veto
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

// ── The thread ──────────────────────────────────────────────────────────────

export interface ThreadViewProps {
  pointId: string;
  /** The point's author: they cannot veto it. */
  pointUserId: string;
  role: PlanRole | null;
  userId: string | null;
  authorityId: string | null;
  /** May post here: a non-viewer on a plan that is not archived. Locked plans keep discussion open. */
  canDiscuss: boolean;
  members: PlanMember[];
  /** The veto composer is open at the top of the thread. */
  vetoing: boolean;
  onVetoClose: () => void;
  /** Put the cursor in the comment box when the thread opens from "Reply". */
  focusComposer: boolean;
  /** After any post here lands. */
  onWrote: () => void;
  /** Changes when someone writes on this topic; the thread refetches without a skeleton. */
  refresh?: number;
}

/**
 * Two levels under a point: top-level comments and vetoes, then replies indented
 * under each. The server clamps depth, so a reply to a reply hangs off its top message.
 */
const ThreadView: React.FC<ThreadViewProps> = ({
  pointId,
  pointUserId,
  role,
  userId,
  authorityId,
  canDiscuss,
  members,
  vetoing,
  onVetoClose,
  focusComposer,
  onWrote,
  refresh,
}) => {
  const toast = useToast();
  const [items, setItems] = useState<ThreadItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [replyTo, setReplyTo] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoadError(null);
    apiPoints
      .listThread(pointId)
      .then((res) => {
        if (live) setItems(res?.items ?? []);
      })
      .catch((e) => {
        if (live) setLoadError(errorMessage(e, 'Could not load the discussion.'));
      });
    return () => {
      live = false;
    };
  }, [pointId, attempt, refresh]);

  const post = async (input: PostThreadInput, fallback: string): Promise<boolean> => {
    try {
      const created = await apiPoints.postThread(pointId, input);
      if (created) setItems((prev) => [...(prev ?? []), created]);
      onWrote();
      return true;
    } catch (e) {
      toast.error(errorMessage(e, fallback));
      return false;
    }
  };

  const mayLift = (veto: ThreadItem) =>
    canDiscuss && ((!!userId && veto.author_id === userId) || canManage(role) || (!!userId && authorityId === userId));

  const all = items ?? [];
  const tops = all.filter((i) => !i.parent_id && i.kind !== 'veto_lift');
  const repliesOf = (id: string) => all.filter((i) => i.parent_id === id && i.kind === 'comment');
  const liftOf = (id: string) => all.find((i) => i.parent_id === id && i.kind === 'veto_lift');

  return (
    <div className="ml-1 mt-1 space-y-1.5 border-l border-outline-variant pb-1 pl-3">
      <Reveal show={!!vetoing && canDiscuss && userId !== pointUserId}>
        <div className="-m-1 p-1">
        <VetoComposer
          onCancel={onVetoClose}
          onSubmit={async (reason) => {
            const ok = await post({ body: reason, kind: 'veto' }, 'Could not record the veto.');
            if (ok) onVetoClose();
            return ok;
          }}
        />
        </div>
      </Reveal>

      <FadeIn key={loadError ? 'error' : items === null ? 'loading' : tops.length === 0 && !vetoing ? 'empty' : 'list'}>
        {loadError ? (
          <Alert variant="note" onRetry={() => setAttempt((n) => n + 1)}>{loadError}</Alert>
        ) : items === null ? (
          <div aria-busy="true">
            <SkeletonGroup label="Loading the discussion" className="space-y-1.5 py-1">
              <Skeleton className="h-3 w-2/3 rounded" />
              <Skeleton className="h-3 w-1/2 rounded" />
            </SkeletonGroup>
          </div>
        ) : tops.length === 0 && !vetoing ? (
          <EmptyState variant="line" dense>
            {canDiscuss ? 'No replies yet. Ask a question or build on the note.' : 'No replies yet.'}
          </EmptyState>
        ) : (
          <ul className="space-y-1.5" aria-label="Discussion">
            {tops.map((item) => {
              const lift = item.kind === 'veto' ? liftOf(item.id) : undefined;
              const replies = repliesOf(item.id);
              return (
                <li key={item.id}>
                  {item.kind === 'veto' ? (
                    <VetoRow
                      item={item}
                      lifted={!!lift || !!item.lifted}
                      liftedBy={lift?.user_name}
                      canLift={mayLift(item)}
                      onLift={async () => {
                        await post({ body: LIFT_BODY, kind: 'veto_lift', parent_id: item.id }, 'Could not lift the veto.');
                      }}
                    />
                  ) : (
                    <div className="px-2 py-1">
                      <Byline item={item} />
                      <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-on-surface">{item.body}</p>
                    </div>
                  )}

                  {replies.length > 0 && (
                    <ul className="ml-4 mt-0.5 space-y-0.5" aria-label="Replies">
                      {replies.map((r) => (
                        <li key={r.id} className="px-2 py-1">
                          <Byline item={r} />
                          <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-on-surface">{r.body}</p>
                        </li>
                      ))}
                    </ul>
                  )}

                  {canDiscuss &&
                    (replyTo === item.id ? (
                      <div className="ml-4 mt-1">
                        <Composer
                          label={`Reply to ${item.user_name ?? 'this message'}`}
                          placeholder="Reply. Type @ to mention someone."
                          members={members}
                          userId={userId}
                          autoFocus
                          submitLabel="Reply"
                          onCancel={() => setReplyTo(null)}
                          onPost={async (body, ids) => {
                            const ok = await post(
                              { body, parent_id: item.id, ...(ids.length ? { mention_user_ids: ids } : {}) },
                              'Could not post the reply.',
                            );
                            if (ok) setReplyTo(null);
                            return ok;
                          }}
                        />
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setReplyTo(item.id)}
                        aria-label={`Reply to ${item.user_name ?? 'this message'}`}
                        className={`${KEY} ml-2`}
                      >
                        Reply
                      </button>
                    ))}
                </li>
              );
            })}
          </ul>
        )}
      </FadeIn>

      {canDiscuss && items !== null && (
        <Composer
          label="Comment on this note"
          placeholder="Comment. Type @ to mention someone."
          members={members}
          userId={userId}
          autoFocus={focusComposer && !vetoing}
          submitLabel="Comment"
          onPost={(body, ids) => post({ body, ...(ids.length ? { mention_user_ids: ids } : {}) }, 'Could not post the comment.')}
        />
      )}
    </div>
  );
};

export default ThreadView;
